import React, { forwardRef } from "react";

const GROUP_COLORS = [
  { bg: "#dcfce7", color: "#15803d" },
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#fef3c7", color: "#b45309" },
  { bg: "#fce7f3", color: "#be185d" },
  { bg: "#ede9fe", color: "#6d28d9" },
  { bg: "#cffafe", color: "#0e7490" },
  { bg: "#fee2e2", color: "#b91c1c" },
  { bg: "#f0fdf4", color: "#166534" },
];

const COLUMNS = "2fr 1.5fr 0.8fr 1.2fr 2fr 0.8fr";
const HEADERS = ["Email", "Assigned By", "User Type", "Roles", "User Group", "Actions"];

const cardStyle = {
  background: "#fff",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  overflow: "hidden",
};

const headerRowStyle = {
  display: "grid",
  gridTemplateColumns: COLUMNS,
  background: "var(--app-sidebar-bg, #a70e13)",
  borderBottom: "2px solid #e5e7eb",
};

const headerCellStyle = {
  padding: "12px 16px",
  fontSize: "11px",
  fontWeight: 600,
  color: "#ffffff",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};

const cellStyle = { padding: "12px 16px", display: "flex", alignItems: "center" };

// ── Skeleton ──────────────────────────────────────────────────────────────────
export const UserTableSkeleton = () => (
  <div style={cardStyle}>
    <div style={headerRowStyle}>
      {HEADERS.map((h) => <div key={h} style={headerCellStyle}>{h}</div>)}
    </div>
    {Array.from({ length: 6 }, (_, i) => (
      <div key={i} style={{ display: "grid", gridTemplateColumns: COLUMNS, borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
        {[1, 2, 3, 4, 5, 6].map((j) => (
          <div key={j} style={{ padding: "14px 16px" }}>
            <div style={{ height: "14px", background: "#f1f5f9", borderRadius: "6px", width: j === 3 ? "60%" : j === 6 ? "70%" : "85%", animation: "pulse 1.5s ease-in-out infinite" }} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// ── Main Table ─────────────────────────────────────────────────────────────────
const UserTable = forwardRef(({ rows, userGroupMap = {}, emptyMessage = "No users available", onEdit, onDelete }, ref) => (
  <div style={cardStyle}>
    <div ref={ref} style={{ width: "100%" }}>
      <div style={headerRowStyle}>
        {HEADERS.map((h) => <div key={h} style={headerCellStyle}>{h}</div>)}
      </div>

      {(!rows || rows.length === 0) && (
        <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>{emptyMessage}</div>
      )}

      {rows && rows.map((item, index) => (
        <div
          key={index}
          style={{ display: "grid", gridTemplateColumns: COLUMNS, borderBottom: "1px solid #f1f5f9", background: index % 2 === 0 ? "#fff" : "#fafbfc", transition: "background 0.12s" }}
          onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
          onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? "#fff" : "#fafbfc"}
        >
          {/* Email */}
          <div style={cellStyle}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#e0f2fe", color: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, marginRight: "10px", flexShrink: 0 }}>
              {item.emailAddress?.[0]?.toUpperCase()}
            </div>
            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.emailAddress}>
              {item.emailAddress?.toLowerCase()}
            </span>
          </div>

          {/* Assigned By */}
          <div style={cellStyle}>
            <span style={{ fontSize: "13px", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.createdBy}>
              {item.createdBy}
            </span>
          </div>

          {/* User Type */}
          <div style={cellStyle}>
            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: item.userType === "Admin" ? "#dbeafe" : "#f1f5f9", color: item.userType === "Admin" ? "#1d4ed8" : "#475569" }}>
              {item.userType}
            </span>
          </div>

          {/* Roles */}
          <div style={{ ...cellStyle, flexWrap: "wrap", gap: "4px" }}>
            {item.rlType && item.rlType.length > 0
              ? item.rlType.map(r => (
                <span key={r.RLtypename} style={{ display: "inline-block", padding: "2px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: "#ede9fe", color: "#6d28d9" }}>{r.RLtypename}</span>
              ))
              : <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>
            }
          </div>

          {/* User Group */}
          <div style={{ ...cellStyle, flexWrap: "wrap", gap: "4px" }}>
            {item.userGroup?.map((g, gi) => {
              const label = g.userGroupName || userGroupMap[g.userGroupId] || String(g.userGroupId);
              const clr = GROUP_COLORS[gi % GROUP_COLORS.length];
              return (
                <span key={gi} style={{ display: "inline-block", padding: "2px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: clr.bg, color: clr.color }}>
                  {label}
                </span>
              );
            })}
          </div>

          {/* Actions */}
          <div style={{ ...cellStyle, justifyContent: "center", gap: "6px" }}>
            <button
              style={{ padding: "4px 12px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#eff6ff", cursor: "pointer", color: "#1d4ed8", fontSize: "11px", fontWeight: 600, transition: "all 0.12s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#dbeafe"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#eff6ff"; }}
              onClick={() => onEdit(item.emailAddress, item.userGroup, item.userType, item.rlType)}
            >
              Edit
            </button>
            <button
              style={{ padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", background: "#fff5f5", cursor: "pointer", color: "#dc2626", fontSize: "11px", fontWeight: 600, transition: "all 0.12s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff5f5"; }}
              onClick={() => onDelete(item.emailAddress)}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
));

UserTable.displayName = "UserTable";
export default UserTable;
